import { enableMenuNavigation } from "@utils/accessibility/menu";

// Observes the menu for dynamically added items and binds selection handlers
const observeMenuForItems = (
    menu: HTMLElement,
    handleItemSelection: (
        item: HTMLElement,
        input: HTMLInputElement,
        label: HTMLElement,
        menu: HTMLElement,
        toggle: HTMLElement
    ) => void,
    input: HTMLInputElement,
    label: HTMLElement,
    toggle: HTMLElement
) => {
    const observer = new MutationObserver((mutations, obs) => {
        const items = menu.querySelectorAll<HTMLElement>(".accessible-dropdown-menuitem");
        if (items.length > 0) {
            items.forEach((item) => {
                item.addEventListener("click", () =>
                    handleItemSelection(item, input, label, menu, toggle)
                );
            });
            obs.disconnect(); //  Stop observing once listeners are added
        }
    });

    observer.observe(menu, {
        childList: true,
        subtree: true,
    });
};

// Toggle dropdown visibility
const toggleMenu = (menu: HTMLElement, forceState?: boolean) => {
    const isOpen = menu.classList.contains("active");
    const newState = forceState !== undefined ? forceState : !isOpen;
    const dropdown = menu.closest(".accessible-dropdown") as HTMLElement;
    dropdown.classList.toggle("active", newState);

    const select = dropdown.querySelector(".custom-select") as HTMLElement;
    if (select) {
        select.setAttribute("aria-expanded", String(newState));
    }

    if (newState) {
        const firstItem = menu.querySelector<HTMLElement>('[role="menuitem"]');
        firstItem?.focus();
    }
};

// Handle dropdown item selection
const handleItemSelection = (
    item: HTMLElement,
    input: HTMLInputElement,
    label: HTMLElement,
    menu: HTMLElement,
    select: HTMLElement
) => {
    input.value = item.dataset.value || "";
    label.textContent = item.textContent || "";
    label.classList.add("selected");
    const changeEvent = new Event("change", { bubbles: true });
    input.dispatchEvent(changeEvent);

    handleFormDisplayUpdate(input.value);
    toggleMenu(menu, false);
    select.focus();
};

// Update visible form based on dropdown selection
const handleFormDisplayUpdate = (formId: string) => {
    const forms = document.querySelectorAll(".data-form-view");

    forms.forEach(form => {
        (form as HTMLElement).style.display = "none";

        form.querySelectorAll(".form-field").forEach(f => {
            f.classList.remove("msg-error", "msg-success");
        });

        form.querySelectorAll(".input-field-error").forEach(el => el.remove());
    });

    const selectedForm = document.querySelector(`[data-id="${formId}"]`);
    if (selectedForm) {
        (selectedForm as HTMLElement).style.display = "";
    }
};

// Setup all custom dropdowns
export const setupCustomDropdown = () => {
    document.querySelectorAll(".accessible-dropdown").forEach(dropdown => {
        const toggle = dropdown.querySelector(".accessible-dropdown .dropdown-toggle") as HTMLElement;
        const menu = dropdown.querySelector(".accessible-dropdown-menu") as HTMLElement;
        const input = dropdown.querySelector(".accessible-dropdown input[type='hidden']") as HTMLInputElement;
        const label = dropdown.querySelector(".accessible-dropdown span") as HTMLElement;

        if (!toggle || !menu || !input || !label) {
            return;
        }

        toggle.addEventListener("click", () => toggleMenu(menu));
        enableMenuNavigation(
            menu,
            toggle,
            () => toggleMenu(menu, false),
            (item) => handleItemSelection(item, input, label, menu, toggle)
        );

        // call observer to combat race conditions for dynamically filled dropdowns
        observeMenuForItems(menu, handleItemSelection, input, label, toggle);
    });
};
